/**
 * Mints and caches signaling access keys for the y-webrtc register→key→connect
 * flow. See .claude/accomodate-y-webrtc.md and the y-webrtc repo's
 * docs/y-webrtc-oracle-setup.md §7.8 / §11.
 *
 * Flow: POST { id } to `<host>/register` with the app token (header
 * `x-app-token`) → `{ key, expiresAt }`; then connect the WebSocket as
 * `wss://host/?token=<key>`. Keys are stateless and signed with a shared secret,
 * so one key is valid on *every* configured host — we register once (trying each
 * host until one succeeds) and append `?token=<key>` to all of them. Only the
 * /register call uses headers; browsers can't set WS headers, hence the query
 * param on the connection.
 */

const CACHE_KEY = "yaqadocs.collab.signalingKey";
// Re-register this many seconds before a key's real expiry so a session never
// connects with an about-to-expire key.
const EXPIRY_MARGIN_SEC = 300;

/** Distinct error for a `403` from /register — "not authorized for collaboration". */
export class SignalingNotAuthorizedError extends Error {
  constructor(message = "This identity is not authorized for collaboration on this server.") {
    super(message);
    this.name = "SignalingNotAuthorizedError";
  }
}

export interface SignalingKey {
  key: string;
  /** Unix seconds. */
  expiresAt: number;
}

interface CachedKey extends SignalingKey {
  /** Fingerprint of (token,id): a change re-registers. Not the plaintext id/token. */
  fp: string;
}

let memoryCache: CachedKey | null = null;

/**
 * Non-cryptographic FNV-1a fingerprint of (token,id). Used only to detect a
 * changed token/id so we never persist the plaintext identity to sessionStorage
 * (the prompt: "Don't persist plaintext identity beyond what's needed").
 */
function fingerprint(token: string, id: string): string {
  const s = `${token} ${id}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16);
}

function readCache(): CachedKey | null {
  if (memoryCache) return memoryCache;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw) memoryCache = JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return memoryCache;
}

function writeCache(entry: CachedKey | null): void {
  memoryCache = entry;
  try {
    if (entry) sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    else sessionStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore — in-memory cache still holds for this session */
  }
}

function isFresh(entry: CachedKey, fp: string): boolean {
  return entry.fp === fp && Date.now() / 1000 < entry.expiresAt - EXPIRY_MARGIN_SEC;
}

/** `wss://host[:port][/path]` → `https://host[:port]/register` (`ws://` → `http://`). */
function registerUrlFor(signalingUrl: string): string {
  const u = new URL(signalingUrl);
  const scheme = u.protocol === "wss:" ? "https:" : u.protocol === "ws:" ? "http:" : u.protocol;
  return `${scheme}//${u.host}/register`;
}

async function registerOnce(signalingUrl: string, token: string, id: string): Promise<SignalingKey> {
  const res = await fetch(registerUrlFor(signalingUrl), {
    method: "POST",
    headers: { "content-type": "application/json", "x-app-token": token },
    body: JSON.stringify({ id }),
  });
  if (res.status === 403) throw new SignalingNotAuthorizedError();
  if (!res.ok) throw new Error(`register failed (${res.status})`);
  const data = await res.json();
  if (typeof data?.key !== "string" || typeof data?.expiresAt !== "number") {
    throw new Error("register returned a malformed response");
  }
  return { key: data.key, expiresAt: data.expiresAt };
}

export interface GetKeyOptions {
  /** Signaling base URLs (no `?token`). */
  urls: string[];
  /** The app token gating /register. */
  token: string;
  /** The verified user id (see src/collab/identity.ts). */
  id: string;
  forceRefresh?: boolean;
}

/**
 * Returns a fresh signaling key, re-registering when the cache is missing,
 * near/after expiry, minted for a different token/id, or `forceRefresh` is set.
 * Tries each host's /register in turn (the key works on all of them); if every
 * host fails, throws SignalingNotAuthorizedError when any host returned 403,
 * else a generic Error.
 */
export async function getSignalingKey(opts: GetKeyOptions): Promise<SignalingKey> {
  const { urls, token, id, forceRefresh } = opts;
  const fp = fingerprint(token, id);

  if (!forceRefresh) {
    const cached = readCache();
    if (cached && isFresh(cached, fp)) return { key: cached.key, expiresAt: cached.expiresAt };
  }

  let sawForbidden = false;
  let lastError: unknown = null;
  for (const url of urls) {
    try {
      const minted = await registerOnce(url, token, id);
      writeCache({ ...minted, fp });
      return minted;
    } catch (err) {
      if (err instanceof SignalingNotAuthorizedError) sawForbidden = true;
      lastError = err;
    }
  }
  writeCache(null);
  if (sawForbidden) throw new SignalingNotAuthorizedError();
  throw new Error(
    `Could not obtain a signaling key: ${lastError instanceof Error ? lastError.message : String(lastError)}`
  );
}

/** Append `?token=<key>` (preserving any existing query) to each signaling URL. */
export function appendKeyToUrls(urls: string[], key: string): string[] {
  return urls.map((url) => {
    const u = new URL(url);
    u.searchParams.set("token", key);
    return u.toString();
  });
}

/** Drop the cached key (e.g. after the WS is rejected), forcing a re-register. */
export function clearSignalingKey(): void {
  writeCache(null);
}
